import { useEffect, useState } from 'react';
import { Link } from 'core/i18n/navigation';
import Image, { StaticImageData } from 'next/image';
import { appRoutes } from 'config/routing';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faImage,
  faLinkHorizontal,
  faListCheck,
  faCommentQuote,
  faText,
} from '@fortawesome/pro-solid-svg-icons';
import mockupText from 'assets/images/product-showcase-mockup-text.png';
import mockupQuote from 'assets/images/product-showcase-mockup-quote.png';
import mockupLink from 'assets/images/product-showcase-mockup-link.png';
import mockupImage from 'assets/images/product-showcase-mockup-image.png';
import mockupList from 'assets/images/product-showcase-mockup-list.png';
import classNames from 'classnames';

interface MemoItem {
  buttonText: string;
  icon: React.ReactNode;
  img: StaticImageData;
  title: string;
  subtitle: string;
}

const MemoItems: MemoItem[] = [
  {
    buttonText: 'Text',
    icon: <FontAwesomeIcon icon={faText} />,
    img: mockupText,
    title: 'Bring your ideas to life',
    subtitle:
      'Easily capture and structure your thoughts with a clear text editor designed for maximum productivity.',
  },
  {
    buttonText: 'Quote',
    icon: <FontAwesomeIcon icon={faCommentQuote} />,
    img: mockupQuote,
    title: 'Highlight your insights with style',
    subtitle:
      'Showcase inspiring quotes or key moments with elegant and captivating designs.',
  },
  {
    buttonText: 'Link',
    icon: <FontAwesomeIcon icon={faLinkHorizontal} />,
    img: mockupLink,
    title: 'Connect your ideas with interactive links',
    subtitle:
      'Add clickable links to connect your ideas, articles, or references all in one place.',
  },
  {
    buttonText: 'Image',
    icon: <FontAwesomeIcon icon={faImage} />,
    img: mockupImage,
    title: 'Use images to make your ideas shine',
    subtitle:
      'Upload images to better visualize and organize in a clear and inspiring way, and never lose them.',
  },
  {
    buttonText: 'List',
    icon: <FontAwesomeIcon icon={faListCheck} />,
    img: mockupList,
    title: 'Organize your ideas with intuitive lists',
    subtitle:
      'Create checkbox lists to structure your tasks, projects, or concepts clearly and efficiently.',
  },
];

export const Section4 = () => {
  const [activeItem, setActiveItem] = useState(2);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveItem((prev) => (prev + 1) % MemoItems.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className={styles.container}>
      <h2 className={styles.title.base}>
        List your thoughts, create your memos, and keep them{' '}
        <span className={styles.title.highlightedText}>forever</span>
      </h2>
      <div className={styles.contentWrapper}>
        <div
          className={`${styles.contentCol.base} ${styles.contentCol.buttons}`}
        >
          {MemoItems.map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveItem(index)}
              className={`${styles.contentCol.button.base} ${
                activeItem === index
                  ? styles.contentCol.button.active
                  : 'bg-transparent'
              } ${
                (activeItem + 1 >= MemoItems.length ? 0 : activeItem + 1) ===
                index
                  ? styles.contentCol.button.animatedBorder
                  : ''
              }`}
            >
              <div className="mr-3 w-6 h-6">{item.icon}</div>
              <p className="text-md lg:text-xl">{item.buttonText}</p>
            </button>
          ))}
        </div>
        <div
          className={`${styles.contentCol.base} ${styles.contentCol.content}`}
        >
          <div className={styles.contentCol.imageWrapper}>
            {MemoItems.map((item, index) => (
              <div
                key={index}
                className={`${styles.contentCol.imageTransition} ${
                  activeItem === index
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                }`}
                style={{
                  // 5s * 60 = 300ms
                  // Must match the button animdation .animated-border-button
                  transitionDelay: activeItem === index ? '300ms' : '0ms',
                }}
              >
                <Image
                  src={item.img}
                  alt={`MindFeed ${item.buttonText} memo`}
                  fill
                  className={styles.contentCol.image}
                  sizes="(max-width: 1024px) 100vw, 420px"
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className={`${styles.contentCol.base} ${styles.contentCol.content}`}
        >
          <div className={styles.contentCol.textWrapper}>
            {MemoItems.map((item, index) => (
              <div
                key={index}
                className={`${styles.contentCol.textTransition} ${
                  activeItem === index
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                }`}
                style={{
                  // 5s * 60 = 300ms
                  // Must match the button animdation .animated-border-button
                  transitionDelay: activeItem === index ? '300ms' : '0ms',
                }}
              >
                <h3 className={styles.contentCol.heading}>{item.title}</h3>
                <p className={styles.contentCol.paragraph}>{item.subtitle}</p>
                <Link
                  href={appRoutes.LOGIN}
                  className={styles.contentCol.linkWrapper}
                >
                  <button className={styles.contentCol.linkButton}>
                    <div className="w-6 h-6">
                      <FontAwesomeIcon icon={faArrowRight} />
                    </div>
                  </button>
                  <h4 className={styles.contentCol.linkText}>
                    Explore MindFeed
                  </h4>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container:
    'flex flex-col items-center justify-center mx-auto mt-28 max-w-7xl lg:mt-36',
  title: {
    base: 'text-4xl font-bold text-left md:text-center text-mf-off-black-950 sm:leading-[57.28px] sm:text-5xl lg:max-w-[930px] px-9',
    highlightedText: 'text-mf-blue',
  },
  contentWrapper:
    'flex flex-col items-center w-full justify-between overflow-hidden mt-12 lg:grid lg:grid-cols-3 lg:mt-28',
  contentCol: {
    base: 'flex w-full sm:w-auto lg:w-full lg:w-auto lg:min-w-full no-scrollbar px-5',
    content: 'flex-col items-center justify-center',
    buttons:
      'flex-row justify-start items-start overflow-auto lg:flex-col lg:justify-center lg:items-center',
    button: {
      base: classNames(
        'flex justify-center items-center transition-all duration-1000 ease-in-out rounded-full',
        'border hover:bg-mf-light-gray-100 text-mf-off-black-950 border-mf-gray-400 border-1',
        // Small
        'py-2.5 px-4.5',
        // Large
        'lg:py-3',
        'lg:w-[140px] ml-3 lg:mr-0 lg:mb-[25px]',
      ),
      active: 'bg-mf-light-gray-100 text-mf-off-gray-600 hover:bg-mf-gray-400',
      animatedBorder: 'animated-border-button',
    },
    imageWrapper:
      'flex items-center justify-center p-5 mr-0 mb-5 w-full relative lg:mb-0 min-h-[550px] min-w-[420px] lg:w-auto lg:min-h-[620px]',
    imageTransition:
      'top-0 left-0 right-0 bottom-0 z-10 absolute transition-all duration-1000 ease-in-out',
    image:
      'h-full min-h-[550px] lg:min-h-[580px] min-w-full object-contain lg:object-contain',
    textWrapper:
      'relative min-w-[330px] min-h-[304px] w-full lg:w-auto flex flex-col',
    textTransition:
      'absolute top-0 left-0 z-10 transition-all duration-1000 ease-in-out',
    heading:
      'text-2xl text-mf-off-black-950 font-bold mb-5 lg:text-4xl lg:font-semibold lg:mb-8',
    paragraph: 'text-xl text-mf-off-black-950 mb-10',
    linkWrapper: 'cursor-pointer flex items-center',
    linkButton: 'rounded-full text-white bg-mf-blue p-4 mr-5',
    linkText: 'text-mf-blue text-2xl font-semibold',
  },
};
